import { inject, Injectable, signal } from '@angular/core';
import { Allergens, Categories, Channels, Components, Departments, GiftcardsTypes, Items, Printers, RawMaterials, Suppliers, Vat } from 'tilby-models';
import { ZPayBridge } from '../../digital-payments/z-pay-bridge/z-pay-bridge.model';
import { EntityManagerService } from 'src/app/core';
import { ZPayBridgeService } from '../../digital-payments';

export interface ItemDataMap {
  'categories': Categories[];
  'departments': Departments[];
  'channels': Channels[];
  'suppliers': Suppliers[];
  'allergens': Allergens[];
  'components': Components[];
  'items': Items[];
  'printers': Printers[];
  'non-fiscal-printers': Printers[];
  'document-printers': Printers[];
  'raw_materials': RawMaterials[];
  'vat': Vat[];
  'giftcards-types': GiftcardsTypes[];
  'z-pay-bridge': ZPayBridge.POS[];
  'item-last-movements': any[];
}
export interface FilteredItemDataMap extends ItemDataMap {
  'printersByCategory': Map<number, Printers[]>;
}

export type ItemDataSetKey = keyof ItemDataMap;
export type ItemDataSetValue = ItemDataMap[ItemDataSetKey];

export type AllItemDataSetSetKey = keyof FilteredItemDataMap;
export type AllItemDataSetValue = FilteredItemDataMap[AllItemDataSetSetKey];

export interface UpdateSession {
  entityName: ItemDataSetKey;
  expectedCount: number;
  receivedCount: number;
  isMassive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ItemDataCatalogService {

  readonly isReady = signal(false);

  private readonly entityManager = inject(EntityManagerService);
  private readonly zPayBridge = inject(ZPayBridgeService);

  private _itemDataCatalog = new Map<ItemDataSetKey, ItemDataSetValue>();

  get itemDataCatalog(): Map<ItemDataSetKey, ItemDataSetValue> {
    return this._itemDataCatalog;
  }

  private _itemDataCatalogFiltered = new Map<AllItemDataSetSetKey, AllItemDataSetValue>();

  get itemDataCatalogFiltered(): Map<AllItemDataSetSetKey, AllItemDataSetValue> {
    return this._itemDataCatalogFiltered;
  }

  addDataToCatalog(key: ItemDataSetKey, value: ItemDataSetValue): void {
    this._itemDataCatalog.set(key, value);
  }

  getDataByKey<K extends ItemDataSetKey>(key: K): ItemDataMap[K] | undefined {
    const originalArray =  this._itemDataCatalog.get(key);
    if (originalArray) {
      return structuredClone(originalArray) as ItemDataMap[K];
    }
    return undefined;
  }

  updateFilteredDataToCatalog(key: AllItemDataSetSetKey, value: AllItemDataSetValue): void {
    this._itemDataCatalogFiltered.set(key, value);
  }

  getFilteredDataByKey<K extends AllItemDataSetSetKey>(key: K): FilteredItemDataMap[K] | undefined {
    const originalArray =  this._itemDataCatalogFiltered.get(key);
    if (originalArray) {
      return structuredClone(originalArray) as FilteredItemDataMap[K];
    }
    return undefined;
  }

  getPrintersByCategory(): Map<number, Printers[]> | undefined {
    const printersByCategory = new Map<number, Printers[]>()
    const allPrinters = this._itemDataCatalog.get("printers");
    if(allPrinters) {
      allPrinters.forEach((printer: any) => {
        printer.categories?.forEach((category: Categories) => {
          if(category.id) {
            if (!printersByCategory.has(category.id)) {
              printersByCategory.set(category.id, []);
            }
            printersByCategory.get(category.id)!.push(printer);
          }
        })
      })
      return printersByCategory;
    }
    return undefined;
  }

  getExtendedCategories(): Categories[] | undefined {
    const originalArray =  this._itemDataCatalog.get("categories");
    if (originalArray) {
      const categoriesWithDefault = structuredClone(originalArray) as Categories[];
      categoriesWithDefault.unshift({ name: 'Nessuna categoria', id: 0 });
      return categoriesWithDefault;
    }
    return undefined;
  }

  getExtendedDepartments(): Departments[] | undefined {
    const originalArray =  this._itemDataCatalog.get("departments");
    if (originalArray) {
      const departmentsWithDefault = structuredClone(originalArray) as Departments[];
      departmentsWithDefault.unshift({ name: 'Nessuno', vat_id: 0, id: 0 });
      return departmentsWithDefault;
    }
    return undefined;
  }

  async loadZPayBridgePOS() {
    try {
      const pmCollection = await this.entityManager.paymentMethods.fetchCollectionOffline({ 
        payment_method_type_id: 42 
      });
      const zPayBridgePayment = pmCollection[0];

      if (!zPayBridgePayment?.schema_name) {
        return; 
      }

      const response = await this.zPayBridge.getPOSList(zPayBridgePayment.schema_name);
      
      this.addDataToCatalog("z-pay-bridge", response)
    } catch (error) {
      console.error("Errore caricamento POS Bridge:", error);
    }
  }


  // GESTORE DI SESSIONE
  private currentSession = signal<UpdateSession | null>(null);
  public readonly currentSessionSignal = this.currentSession.asReadonly();

  getCurrentSession(): UpdateSession | null {
    return this.currentSession();
  }

  isEntityInSession(entityName: ItemDataSetKey): boolean {
    const session = this.currentSession();
    return session?.entityName === entityName;
  }

  public updateCompletedSignal = signal<{entityName: string, timestamp: number} | null>(null);

  startUpdateSession(entityName: ItemDataSetKey, count: number) {
    this.currentSession.set({
      entityName,
      expectedCount: count,
      receivedCount: 0,
      isMassive: count > 10
    });
  }

  registerUpdate(entityName: ItemDataSetKey, isMassiveNotification: boolean) {
    const session = this.currentSession();
    if (!session || session.entityName !== entityName) return;

    if (isMassiveNotification || session.isMassive) {
      this.completeSession();
    } else {
      session.receivedCount++;
      if (session.receivedCount >= session.expectedCount) {
        this.completeSession();
      }
    }
  }

  private completeSession() {
    const session = this.currentSession();
    this.updateCompletedSignal.set({ 
      entityName: session!.entityName, 
      timestamp: Date.now() 
    });
    this.currentSession.set(null);
  }

  closeSession() {
    this.updateCompletedSignal.set(null);
  }
  //

}